import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonModal,
  IonPage,
  IonToolbar,
  createGesture,
} from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import Nav from './Nav';
import ContactCard from './ContactCard';
import HeroChrome from '../components/HeroChrome';
import HomeAlerts from '../components/HomeAlerts';
import { ChevronRight } from '../components/icons';
import {
  AccidentSvg,
  ChainOfContactSvg,
  DmPhoneSvg,
  FaqSvg,
  HandbookSvg,
  LicensePlateSvg,
  LoadHistorySvg,
  MoneySvg,
  NewReportSvg,
  OrientationReportSvg,
  PhoneSvg,
  RegistrationsSvg,
  ReleaseTraineeSvg,
  SafetyContactsSvg,
  TraineeProfileSvg,
} from '../components/svgIcons';
import { ApiService } from '../api/ApiService';
import { UserState, userStore } from '../api/AuthService';
import { DriverProfile } from '../api/model/DriverProfile';
import { DriverManager } from '../api/model/DriverManager';
import { PreplannedLoad } from '../api/model/PreplannedLoad';
import { PushNotificationsService } from '../api/PushNotificationsService';
import { sidnavStore, SidenavState } from '../api/SidenavStore';
import { UserUtils } from '../utils/UserUtils';
import { checkOS } from '../components/OSCheck';
import { toastStore } from '../components/AppToast';
import { runStatus } from '../components/walkthrough/FirstSignIn';
import { Capacitor } from '@capacitor/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { config } from '../Constants';
import { useLogPageVisit } from '../utils/logPageVisit';
import { logUserAction } from '../utils/logUserAction';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

function toProperCase(str: string) {
  if (!str) return '';
  return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

type LoadBoardSummary = {
  status: 'loading' | 'available' | 'alreadyLoaded' | 'unavailable';
  count: number;
};

const emptyLoadBoard: LoadBoardSummary = {
  status: 'unavailable',
  count: 0,
};

function summarizeTopLoads(payload: any): LoadBoardSummary {
  const loads = payload && payload.loads != null ? payload.loads : payload;
  if (loads && loads.error === 'Unit already planned') {
    return { status: 'alreadyLoaded', count: 0 };
  }
  if (loads && loads.error === 'Unit not modeled') {
    return emptyLoadBoard;
  }
  const orders = Array.isArray(loads && loads.orders) ? loads.orders : [];
  const usable = orders.filter(
    (order: any) => order && order.orderNumber && order.orderNumber !== 'WAIT' && order.orderNumber !== 'HOME'
  );
  if (!usable.length) return emptyLoadBoard;
  return {
    status: 'available',
    count: Math.min(usable.length, 3),
  };
}

function formatJulianDate(value?: string | number): string {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  if (!/^\d{5,7}$/.test(raw)) return raw;
  const year = Number(raw.slice(0, 4));
  const dayOfYear = Number(raw.slice(4));
  if (!year || !dayOfYear) return raw;
  const d = new Date(year, 0, 1);
  d.setDate(dayOfYear);
  if (isNaN(d.getTime())) return raw;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function formatDeliveryEta(deliveryEndTime?: string): string {
  if (!deliveryEndTime) return '—';
  const raw = String(deliveryEndTime).trim();
  if (!raw) return '—';
  const d = new Date(raw.includes('T') || raw.includes('-') ? raw : raw.replace(' ', 'T'));
  if (isNaN(d.getTime())) {
    return raw.replace(/,?\s*\d{4}\b/, '').replace(/\b\d{4}\b/, '').trim() || '—';
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? ' pm' : ' am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const minStr = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
  return `${months[d.getMonth()]} ${d.getDate()}, ${hours}${minStr}${ampm}`;
}

function useInView<T extends HTMLElement = HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        observer.disconnect();
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

type ContactButtonProps = {
  to?: string;
  onClick?: (e: React.MouseEvent) => void;
  label: string;
  icon: React.ReactNode;
  side: 'left' | 'right';
};

const ContactButton: React.FC<ContactButtonProps> = ({ to, onClick, label, icon, side }) => {
  const { ref, inView } = useInView<HTMLElement>();
  const className = `home-action home-action--contact home-contacts__btn--${side}${
    inView ? ' is-inview' : ''
  }`;
  const inner = (
    <>
      <div className="home-action__icon">{icon}</div>
      <div className="home-action__label">{label}</div>
    </>
  );
  if (to) {
    return (
      <Link ref={ref as any} to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button ref={ref as any} type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
};

const Home: React.FC = () => {
  useLogPageVisit('LP Home', 'User viewed the Home page');
  const history = useHistory();
  const gestureEl = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [showRateInfo, setShowRateInfo] = useState(false);
  const [showDM, setShowDM] = useState(false);
  const [currentRate, setCurrentRate] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [dm, setDM] = useState<DriverManager>({} as DriverManager);
  const [preplannedLoads, setPreplannedLoads] = useState<PreplannedLoad[]>([]);
  const [loadBoard, setLoadBoard] = useState<LoadBoardSummary>({
    ...emptyLoadBoard,
    status: 'loading',
  });
  const [heroFeedsReady, setHeroFeedsReady] = useState(false);

  const { driver, storedLoadInfo } = userStore((state: UserState) => ({
    driver: state.driver as DriverProfile,
    storedLoadInfo: state.loadInfo,
  }));
  const { close } = sidnavStore((state: SidenavState) => ({ close: state.close }));
  const { toggleSidenav } = sidnavStore((state: SidenavState) => ({ toggleSidenav: state.toggle }));

  const hasPreplan = preplannedLoads.length > 0;
  const heroSlideCount = 3;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true, playOnInit: false }) as any
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, axis: 'x', watchDrag: true, dragThreshold: 12 },
    [autoplay.current]
  );

  const stopAutoplay = useCallback(() => {
    if (autoplay.current && typeof autoplay.current.stop === 'function') {
      autoplay.current.stop();
    }
  }, []);

  const onDotClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      stopAutoplay();
    },
    [emblaApi, stopAutoplay]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('pointerDown', stopAutoplay);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('pointerDown', stopAutoplay);
    };
  }, [emblaApi, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    if (!heroFeedsReady) {
      stopAutoplay();
      return;
    }
    emblaApi.scrollTo(0, true);
    if (autoplay.current && typeof autoplay.current.play === 'function') {
      autoplay.current.play();
    }
    return () => {
      stopAutoplay();
    };
  }, [emblaApi, heroFeedsReady, hasPreplan, preplannedLoads.length, loadBoard.status, loadBoard.count, stopAutoplay]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    getAvailableAppVersion();
    if (config.showSidenav) {
      gestureInit();
    }
    console.log('os', checkOS());
    if (localStorage.getItem('pushNotificationPermission') === null) {
      const userId = userStore.getState().user?.id;
      if (userId) {
        PushNotificationsService.registerAfterAppLogin(userId);
      }
    }

    ApiService.chatConversationGet('home', {}).then((it: any) => {
      if (!Array.isArray(it)) return;
      let count = 0;
      for (let i = 0; i < it.length; i++) {
        if (it[i]?.lastMessage?.read !== 1) count += 1;
      }
      setMsgCount(count);
    }).catch(() => {});

    if (!UserUtils.isAdmin() && !UserUtils.isDriverManager() && !UserUtils.isInspector()) {
      ApiService.getPushNotifications('home')
        .then((it: any) => {
          if (!Array.isArray(it)) return;
          setNotificationCount(it.filter((notification: any) => !notification.read).length);
        })
        .catch(() => {});
    }

    if (userStore.getState().driver?.dmCode) {
      ApiService.getDriverManager('home', { dmCode: userStore.getState().driver.dmCode }).then(it => {
        if (it) setDM(it);
      });
    }

    if (localStorage.getItem('type') === 'trainee') {
      ApiService.getPayMatrix('home', {}).then((it: any) => {
        if (it == null) return;
        setCurrentRate(Math.round(Number(it) * 100) / 100);
      });
    }

    let cancelled = false;
    const preplanned = ApiService.getPreplannedLoads('home')
      .then(it => {
        if (!cancelled) setPreplannedLoads(Array.isArray(it) ? it : []);
      })
      .catch(() => {
        if (!cancelled) setPreplannedLoads([]);
      });

    const unit = userStore.getState().driver?.unit;
    const board = unit
      ? ApiService.topLoadsGet('home', { unit })
          .then(it => {
            if (!cancelled) setLoadBoard(summarizeTopLoads(it));
          })
          .catch(() => {
            if (!cancelled) setLoadBoard(emptyLoadBoard);
          })
      : Promise.resolve().then(() => {
          if (!cancelled) setLoadBoard(emptyLoadBoard);
        });

    Promise.all([preplanned, board]).then(() => {
      if (!cancelled) setHeroFeedsReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const getAvailableAppVersion = async () => {
    try {
      const result = await AppUpdate.getAppUpdateInfo();
      if (Capacitor.getPlatform() === 'android') {
        console.log('code', result.availableVersionCode);
      } else {
        console.log('name', result.availableVersionName);
      }
    } catch (error) {
      console.log('Error getting version', error);
    }
  };

  const gestureInit = () => {
    const card = gestureEl.current;
    if (!card) return;
    const gesture = createGesture({
      el: card,
      gestureName: 'swipeMenu',
      onMove: () => {},
      onEnd: () => {
        runStatus ? console.log('locked') : toggleSidenav();
      },
    });
    gesture.enable();
  };

  const greetingName = useMemo(() => {
    const name = driver?.name || '';
    const first = name.indexOf(' ') > 0 ? name.substring(0, name.indexOf(' ')) : name;
    return toProperCase(first);
  }, [driver]);

  const hasLoad = !!storedLoadInfo;
  const destination = hasLoad
    ? [storedLoadInfo!.consigneeCity, storedLoadInfo!.consigneeState].filter(Boolean).join(', ') || '—'
    : 'No active load';
  const loadNumberLabel = hasLoad
    ? `Load #${storedLoadInfo!.orderNumber || storedLoadInfo!.loadNumber || '—'}`
    : '';
  const loadOriginMeta = hasLoad
    ? `from ${[storedLoadInfo!.shipperCity, storedLoadInfo!.shipperState].filter(Boolean).join(', ') || '—'}`
    : 'Check Load for available trips';
  const toDestination =
    hasLoad && storedLoadInfo!.milesToDestination != null
      ? Number(storedLoadInfo!.milesToDestination)
      : null;
  const totalTrip = hasLoad && storedLoadInfo!.milesTotal != null ? Number(storedLoadInfo!.milesTotal) : null;
  const milesToGo =
    toDestination != null && !Number.isNaN(toDestination) ? String(Math.round(toDestination)) : '—';
  const totalTripMiles =
    totalTrip != null && !Number.isNaN(totalTrip) ? String(Math.round(totalTrip)) : '—';
  const estEta = hasLoad ? formatDeliveryEta(storedLoadInfo!.deliveryEndTime) : '—';

  const nextPreplan = preplannedLoads[0];
  const preplanLoadLabel = hasPreplan
    ? `Load #${nextPreplan.orderNumber || nextPreplan.loadNumber || '—'}`
    : '';
  const preplanDestination = hasPreplan
    ? [nextPreplan.consigneeCity, nextPreplan.consigneeState].filter(Boolean).join(', ') || '—'
    : 'No preplanned load';
  const preplanOriginMeta = hasPreplan
    ? `from ${[nextPreplan.shipperCity, nextPreplan.shipperState].filter(Boolean).join(', ') || '—'}`
    : 'Tap to view upcoming assignments';
  const preplanPickup = hasPreplan ? formatJulianDate(nextPreplan.pickupStartDate) : '—';
  const preplanCountLabel = String(preplannedLoads.length);

  const loadBoardDestination =
    loadBoard.status === 'available'
      ? 'Loads available'
      : loadBoard.status === 'alreadyLoaded'
        ? 'Already loaded'
        : loadBoard.status === 'loading'
          ? 'Checking load board'
          : 'No loads available';
  const loadBoardOriginMeta =
    loadBoard.status === 'available'
      ? 'There are loads to select from'
      : loadBoard.status === 'alreadyLoaded'
        ? 'Finish your current haul to pick a new one'
        : loadBoard.status === 'loading'
          ? 'Checking your load board'
          : 'Tap to view load selection';
  const loadBoardCountLabel = String(loadBoard.count);

  const dmPhone = dm.phone || dm.dmPhone;
  const callAgent = () => {
    if (!dmPhone) {
      toastStore.getState().setToast('Agent phone number is not available.');
      return;
    }
    logUserAction('LP Call Agent', 'User called their agent from home');
    window.location.href = `tel:${dmPhone}`;
  };

  const onLoadBoardActivate = useCallback(() => {
    stopAutoplay();
    logUserAction('LP Home Load Selection', 'User opened Load Selection from the home carousel');
    history.push('/loadselection');
  }, [history, stopAutoplay]);

  const onPreplanActivate = useCallback(() => {
    stopAutoplay();
    logUserAction('LP Home Preplanned', 'User opened Preplanned from the home carousel');
    history.push('/preplanned');
  }, [history, stopAutoplay]);

  const onHaulActivate = useCallback(() => {
    stopAutoplay();
    if (hasLoad) history.push('/currentload');
  }, [hasLoad, history, stopAutoplay]);

  const trainingTiles = !UserUtils.isDriver()
    ? [
        ...(!UserUtils.isTrainee()
          ? [
              { label: 'New Report', to: '/trainingday', icon: <NewReportSvg />, delay: 'home-delay-110' },
              { label: 'Release Trainee', to: '/releasetrainee', icon: <ReleaseTraineeSvg />, delay: 'home-delay-115' },
            ]
          : []),
        { label: 'Agreements', to: '/agreements', icon: <HandbookSvg />, delay: 'home-delay-120' },
        { label: 'Training Contacts', to: '/trainingcontacts', icon: <PhoneSvg />, delay: 'home-delay-125' },
        UserUtils.isTrainee()
          ? {
              label: 'Trainee Profile',
              to: '/traineeprofile',
              icon: <TraineeProfileSvg />,
              span2: !UserUtils.isTrainerFeedbackEnabled(),
              delay: 'home-delay-130',
            }
          : { label: 'Trainee Info', to: '/traineeprofile', icon: <TraineeProfileSvg />, span2: true, delay: 'home-delay-130' },
        ...(UserUtils.isTrainerFeedbackEnabled()
          ? [{ label: 'Trainer Feedback', to: '/trainerfeedback', icon: <FaqSvg />, span2: true, delay: 'home-delay-130' }]
          : []),
      ]
    : [];

  const loadSelectionSlide = (
    <div className="home-hero__carousel-slide" key="load-selection">
      <div
        role="link"
        tabIndex={0}
        className="home-hero__content home-hero__content--haul-link"
        aria-label="View load selection"
        onClick={e => {
          e.stopPropagation();
          onLoadBoardActivate();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onLoadBoardActivate();
          }
        }}
      >
        <div className="home-hero__haul">
          <div className="home-hero__eyebrow">Load board</div>
          <div className="home-hero__destination home-display">{loadBoardDestination}</div>
          <div className="home-hero__meta">{loadBoardOriginMeta}</div>
        </div>
        <div className="home-hero__chips">
          <div className="home-hero__chips-row">
            <div className="home-hero__chip">
              <div className="home-hero__chip-value">{loadBoardCountLabel}</div>
              <div className="home-hero__chip-label">
                {loadBoard.count === 1 ? 'available load' : 'available loads'}
              </div>
            </div>
          </div>
          <div className="home-hero__haul-hint">
            tap for more
            <ChevronRight color="#ffffff" />
          </div>
        </div>
      </div>
    </div>
  );

  const preplanSlide = (
    <div className="home-hero__carousel-slide" key="preplanned">
      <div
        role="link"
        tabIndex={0}
        className="home-hero__content home-hero__content--haul-link"
        aria-label="View preplanned loads"
        onClick={e => {
          e.stopPropagation();
          onPreplanActivate();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPreplanActivate();
          }
        }}
      >
        <div className="home-hero__haul">
          {preplanLoadLabel ? <div className="home-hero__load-number">{preplanLoadLabel}</div> : null}
          <div className="home-hero__eyebrow">Next preplanned</div>
          <div className="home-hero__destination home-display">{preplanDestination}</div>
          <div className="home-hero__meta">{preplanOriginMeta}</div>
        </div>
        <div className="home-hero__chips">
          <div className="home-hero__chips-row">
            <div className="home-hero__chip">
              <div className="home-hero__chip-value">{preplanCountLabel}</div>
              <div className="home-hero__chip-label">
                {preplannedLoads.length === 1 ? 'upcoming load' : 'upcoming loads'}
              </div>
            </div>
            <div className="home-hero__chip">
              <div className="home-hero__chip-value home-hero__chip-value--date">{preplanPickup}</div>
              <div className="home-hero__chip-label">pickup</div>
            </div>
          </div>
          <div className="home-hero__haul-hint">
            tap for more
            <ChevronRight color="#ffffff" />
          </div>
        </div>
      </div>
    </div>
  );

  const currentLoadSlide = (
    <div className="home-hero__carousel-slide" key="current-load">
      <div
        role="link"
        tabIndex={0}
        className="home-hero__content home-hero__content--haul-link"
        aria-label="View current load"
        onClick={e => {
          e.stopPropagation();
          onHaulActivate();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onHaulActivate();
          }
        }}
      >
        <div className="home-hero__haul">
          {loadNumberLabel ? <div className="home-hero__load-number">{loadNumberLabel}</div> : null}
          <div className="home-hero__eyebrow">Currently hauling to</div>
          <div className="home-hero__destination home-display">{destination}</div>
          <div className="home-hero__meta">{loadOriginMeta}</div>
        </div>
        <div className="home-hero__chips">
          <div className="home-hero__chips-row">
            <div className="home-hero__chip">
              <div className="home-hero__chip-value">{milesToGo}</div>
              <div className="home-hero__chip-label">miles to go</div>
            </div>
            <div className="home-hero__chip">
              <div className="home-hero__chip-value">{totalTripMiles}</div>
              <div className="home-hero__chip-label">est. trip miles</div>
            </div>
          </div>
          <div className="home-hero__haul-hint">
            tap for more
            <ChevronRight color="#ffffff" />
          </div>
        </div>
      </div>
    </div>
  );

  const heroSlides = hasPreplan
    ? [currentLoadSlide, preplanSlide, loadSelectionSlide]
    : [loadSelectionSlide, currentLoadSlide, preplanSlide];

  return (
    <IonPage>
      <IonContent fullscreen className="design-a-content">
        <HomeAlerts />

        <IonModal isOpen={showRateInfo} cssClass="small-modal" onDidDismiss={() => setShowRateInfo(false)}>
          <IonHeader>
            <IonToolbar color="primary">
              <p style={{ fontFamily: 'Montserrat, sans-serif', padding: '0 8px' }} onClick={() => setShowRateInfo(false)}>
                Close
              </p>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="item-container" style={{ padding: 16 }}>
              <p style={{ fontSize: 24, fontWeight: 600, color: '#666667', textAlign: 'center' }}>Rate Info</p>
              <p style={{ fontSize: 32, fontWeight: 600, textAlign: 'center' }}>
                {currentRate ? `$${currentRate}` : '—'}
              </p>
              <p className="subtitle">
                ***To be paid for the day, you must log drive time or on duty time on the tablet. If you are in
                sleeper birth or off duty all day, you will not be paid. If you have any questions, please contact
                your training manager.
              </p>
            </div>
          </IonContent>
        </IonModal>

        <IonModal isOpen={showDM} id="transparent" onDidDismiss={() => setShowDM(false)}>
          <div className="modal-div" onClick={() => setShowDM(false)}>
            <div className="item-container" id="modal">
              <ContactCard
                contact={{
                  name: `${dm.firstName || ''} ${dm.lastName || ''}`.trim() || dm.dmName || 'Agent',
                  email: dm.email || dm.dmEmail,
                  subcategory: 'Driver Agent',
                  phone: dmPhone,
                }}
              />
            </div>
          </div>
        </IonModal>

        <div
          ref={gestureEl}
          className={`design-a${mounted ? ' design-a--mounted' : ''}`}
          onClick={() => close()}
          id="WalkThroughFeature0"
        >
          <div className="home-hero" id="home-hero">
            <HeroChrome
              onMenuClick={() => toggleSidenav()}
              msgCount={msgCount}
              notificationCount={notificationCount}
            />
            <div className="home-hero__body" onClick={e => e.stopPropagation()}>
              <div className="home-hero__greeting">
                Hey {greetingName || 'Driver'}! · Unit {driver?.unit || '—'}
              </div>
              <div className="home-hero__carousel" ref={emblaRef} id="WalkThroughFeature2">
                <div className="home-hero__carousel-container">
                  {heroSlides}
                </div>
              </div>
              <div className="home-hero__dots" role="tablist" aria-label="Home slides">
                {Array.from({ length: heroSlideCount }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-label={`Go to slide ${index + 1}`}
                    aria-selected={index === selectedIndex}
                    className={`home-hero__dot${index === selectedIndex ? ' is-active' : ''}`}
                    onClick={() => onDotClick(index)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="home-sheet">
            <div className="load-options-grid" id="WalkThroughFeature1">
              {UserUtils.isTrainee() ? (
                <button
                  type="button"
                  className="load-option"
                  onClick={e => {
                    e.stopPropagation();
                    setShowRateInfo(true);
                  }}
                >
                  <div className="load-option__icon">
                    <MoneySvg />
                  </div>
                  <div>
                    <div className="load-option__title">Rate Info</div>
                    <div className="load-option__sub">View your current pay rate</div>
                  </div>
                </button>
              ) : null}

              <Link to="/registrations" className={`load-option${UserUtils.isTrainee() ? '' : ' load-option--span2'}`}>
                <div className="load-option__icon">
                  <RegistrationsSvg />
                </div>
                <div>
                  <div className="load-option__title">View Registrations</div>
                  <div className="load-option__sub">Vehicle registration status</div>
                </div>
              </Link>
            </div>

            {!UserUtils.isDriver() && !UserUtils.isTrainee() ? (
              <>
                <div className="home-section-label">Orientation</div>
                <div className="home-training-grid">
                  <Link to="/orientationroadtests" className="home-training-tile home-delay-65">
                    <div className="home-training-tile__icon">
                      <OrientationReportSvg />
                    </div>
                    <div className="home-training-tile__label">Road Tests</div>
                  </Link>
                  <Link to="/retest" className="home-training-tile home-delay-70">
                    <div className="home-training-tile__icon">
                      <LoadHistorySvg />
                    </div>
                    <div className="home-training-tile__label">Re-test</div>
                  </Link>
                </div>
              </>
            ) : null}

            {trainingTiles.length > 0 ? (
              <>
                <div className="home-section-label">Training</div>
                <div className="home-training-grid">
                  {trainingTiles.map(tile => (
                    <Link
                      key={tile.to + tile.label}
                      to={tile.to}
                      className={`home-training-tile ${tile.span2 ? 'home-training-tile--span2' : ''} ${tile.delay}`}
                    >
                      <div className="home-training-tile__icon">{tile.icon}</div>
                      <div className="home-training-tile__label">{tile.label}</div>
                    </Link>
                  ))}
                </div>
              </>
            ) : null}

            <div className="home-contacts" id="WalkThroughFeature3">
              <div className="home-contacts__title">Looking to get in touch?</div>
              <button
                type="button"
                className="home-action home-action--contact home-contacts__call-dm home-delay-100"
                onClick={e => {
                  e.stopPropagation();
                  callAgent();
                }}
              >
                <div className="home-action__icon">
                  <DmPhoneSvg />
                </div>
                <div className="home-action__label">Call Agent</div>
                <ChevronRight />
              </button>
              <button
                type="button"
                className="home-action home-action--contact"
                onClick={e => {
                  e.stopPropagation();
                  setShowDM(true);
                }}
              >
                <div className="home-action__icon">
                  <TraineeProfileSvg />
                </div>
                <div className="home-action__label">View Agent Info</div>
                <ChevronRight />
              </button>
              <div className="home-contacts__subtitle">Need more specific help?</div>
              <div className="home-contacts__pair">
                <ContactButton to="/chainofcommand" label="Company Directory" icon={<ChainOfContactSvg />} side="left" />
                <ContactButton to="/safetycontacts" label="Safety Contact" icon={<SafetyContactsSvg />} side="right" />
              </div>
              <div className="home-contacts__pair">
                <ContactButton to="/helpdesk" label="FAQ" icon={<FaqSvg />} side="left" />
                <ContactButton to="/accidentinfo" label="Accidents" icon={<AccidentSvg />} side="right" />
              </div>   
            </div>
            <Link to="/registervehicle" className="home-register home-delay-125">
              <div className="home-action__icon">
                <LicensePlateSvg />
              </div>
              <div className="home-register__label">Register personal vehicle</div>
              <ChevronRight />
            </Link>
          </div>
        </div>
      </IonContent>
      <IonFooter>
        <Nav pageName="Home" />
      </IonFooter>
    </IonPage>
  );
};

export default Home;
